import {
  ComponentPropsWithoutRef,
  ReactElement,
  ReactNode,
  useEffect,
} from 'react';

import { styled } from '@mui/material';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import clsx from 'clsx';

import { useMergedRef } from '@cast/utils';

import { Sizes, WithRef } from '../../../types';
import { Input, InputProps } from '../input';

/**
 * Props for the TextArea component.
 * Extends InputProps with textarea-specific properties while omitting conflicting props.
 */
export type TextAreaProps = {
  /** Icon element to display within the textarea */
  icon?: ReactNode | ReactElement;
  /** Whether the textarea is read-only */
  readOnly?: boolean;
  /** Whether to disable textarea resizing. Defaults to true */
  disableResize?: boolean;
  /** Whether the textarea should be responsive to container size */
  responsive?: boolean;
  /** Minimum number of rows to display */
  minRows?: number;
  /** Maximum number of rows to display */
  maxRows?: number;
  /** Size variant of the textarea */
  size?: Sizes<'sm' | 'md'>;
} & Omit<InputProps, 'multiline' | 'size' | 'icon'>;

const StyledInput = styled(Input, {
  name: 'DsTextArea',
  slot: 'Root',
  overridesResolver: ({ size }, styles) => {
    // `size` is the legacy Input size ('small' | 'medium') passed to Input —
    // map it to the new Sizes-based theme slots
    return [styles.root, styles[size === 'small' ? 'sm' : 'md']];
  },
})({});
StyledInput.displayName = 'StyledInput';

const TextAreaInput = ({
  ref,
  // Prevent warning in console
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ownerState,
  ...props
}: WithRef<
  HTMLTextAreaElement,
  ComponentPropsWithoutRef<typeof TextareaAutosize> & { ownerState?: unknown }
>) => {
  return <TextareaAutosize {...props} ref={ref} />;
};
TextAreaInput.displayName = 'TextAreaInput';

const TextArea = ({
  ref,
  className,
  label,
  labelTooltipProps,
  icon,
  success,
  readOnly,
  hint,
  error,
  disableResize = true,
  responsive = false,
  minRows = 2,
  maxRows = 2,
  size = 'md',
  sx,
  InputProps,
  ...props
}: WithRef<HTMLDivElement, TextAreaProps>) => {
  const [textFieldRef, setRef] = useMergedRef(ref);
  const textFieldEl = textFieldRef?.current;

  // Input has not migrated to the new Sizes type yet — map to its legacy size
  const inputSize: InputProps['size'] = size === 'sm' ? 'small' : 'medium';

  useEffect(() => {
    if (textFieldEl) {
      const { width } = textFieldEl.getBoundingClientRect();
      const textAreas = textFieldEl.getElementsByTagName('textarea');
      for (const textArea of textAreas) {
        textArea.style.width = `${width}px`;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!textFieldEl]);

  useEffect(() => {
    if (responsive && !!textFieldEl) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const width = entry.contentRect.width;
          const textAreas = textFieldEl.getElementsByTagName('textarea');
          for (const textArea of textAreas) {
            textArea.style.width = `${width}px`;
          }
        }
      });

      if (textFieldEl) {
        resizeObserver.observe(textFieldEl);
      }

      return () => {
        if (textFieldEl) {
          resizeObserver.unobserve(textFieldEl);
        }
      };
    }
    return undefined;
  }, [responsive, textFieldEl]);

  return (
    <StyledInput
      ref={setRef}
      className={clsx(
        className,
        'DS-TextArea-root',
        `DS-Input-size-${size}`,
        readOnly && 'DS-Input-readOnly'
      )}
      size={inputSize}
      label={label}
      labelTooltipProps={labelTooltipProps}
      // Required for placeholder to be visible if input is empty and has no adornments
      hint={hint}
      error={error}
      multiline
      inputProps={{ className: clsx(disableResize && 'DS-disableResize') }}
      InputProps={{
        readOnly,
        className: clsx(
          success && 'DS-success',
          readOnly && `DS-Input-readOnly`,
          `DS-TextArea-size-${size}`
        ),
        minRows,
        maxRows,
        endAdornment: icon ? (
          <div className="DS-TextArea-adornment-root">{icon}</div>
        ) : undefined,
        components: { Input: TextAreaInput },
        sx,
        ...InputProps,
      }}
      {...props}
    />
  );
};
TextArea.displayName = 'TextArea';

export default TextArea;
